/**
 * @fileoverview App-level Redux sagas for managing initialization and data fetching.
 * 
 * This module contains sagas that:
 * - Initialize the application on load
 * - Fetch CSA data (all types: All, Monitoring, IM)
 * - Fetch end-of-day (EOD) date
 * - Fetch SIMM version
 * 
 * @module app/sagas
 */

import {
    all, takeEvery, call, put
} from 'redux-saga/effects';
import fp from 'lodash/fp';

import * as actions from './actions';
import * as systemInfoApi from '../api/systemInfoApi';
import * as settingsApi from '../api/settingsApi';
import { setUserRole } from '../settings/actions';
import { createCsaList } from '../api/adapters/systemInfoAdapter';


/**
 * Fetches all CSA data and counterparty information from the API.
 * 
 * This saga fetches three types of CSA data in parallel:
 * 1. All CSAs (general list)
 * 2. Monitoring CSAs
 * 3. IM (Initial Margin) CSAs
 * 
 * It also fetches:
 * - End-of-day (EOD) date
 * - SIMM version
 * 
 * If any CSA has undefined/missing values, a validation error is displayed.
 * 
 * @generator
 * @param {Object} api - System info API service object
 * @yields {call} API calls to fetch CSA data, EOD date, and SIMM version
 * @yields {put} Actions to store fetched data or errors
 * 
 * @example
 * // Called from appLoaded saga
 * yield call(fetchCsasAndCounterparties, systemInfoApi);
 */
export function* fetchCsasAndCounterparties(api) {
    try {
        const csas = yield call(api.csaList);
        const csaList = createCsaList(csas);
        const undefinedCsaList = csaList.filter(fp.includes(undefined));
        if (!undefinedCsaList.length) {
            yield put(actions.fetchCsaDataSucceeded(csaList));
        } else {
            let amount = 0;
            const keyissues = [];
            undefinedCsaList.forEach((csa) => {
                if (csa.csa) {
                    keyissues.push(csa);
                } else {
                    amount += 1;
                }
            });
            yield put(actions.fetchCsasUndefinedError(`
            Error fetching csa list.`,
            `The following CSAs have missing information
            ${keyissues.map(x => `\n${x.csa}`)}
            ${amount} have no csa `));
        }
    } catch (e) {
        yield put(actions.fetchCsaDataFailed(e.message));
    }
    try {
        const monitorCsas = yield call(api.csaMonitorList);
        const monitoringCsaList = createCsaList(monitorCsas);
        const undefinedMonitoringCsa = monitoringCsaList.filter(fp.includes(undefined));
        if (!undefinedMonitoringCsa.length) {
            yield put(actions.fetchMonitoringCsaDataSucceeded(monitoringCsaList));
        } else {
            let amount = 0;
            const keyissues = [];
            undefinedMonitoringCsa.forEach((csa) => {
                if (csa.csa) {
                    keyissues.push(csa);
                } else {
                    amount += 1;
                }
            });
            yield put(actions.fetchCsasUndefinedError(`
            Error fetching csa list.`,
            `The following CSAs have missing information
            ${keyissues.map(x => `\n${x}`)}
            ${amount} have no csa`));
        }
    } catch (e) {
        yield put(actions.fetchMonitoringCsaDataFailed(e.message));
    }
    try {
        const imCsas = yield call(api.imCsaList);
        const imCsaList = createCsaList(imCsas);
        const undefinedImCsas = imCsaList.filter(fp.includes(undefined));
        if (!undefinedImCsas.length) {
            yield put(actions.fetchImCsaDataSucceeded(imCsaList));
        } else {
            let amount = 0;
            const keyissues = [];
            undefinedImCsas.forEach((csa) => {
                if (csa.csa) {
                    keyissues.push(csa);
                } else {
                    amount += 1;
                }
            });
            yield put(actions.fetchCsasUndefinedError(`
            Error fetching csa list.`,
            `The following CSAs have missing information
            ${keyissues.map(x => `\n${x}`)}
            ${amount} have no csa`));
        }
    } catch (e) {
        yield put(actions.fetchImCsaDataFailed(e.message));
    }
    try {
        const eod = yield call(api.eodDate);
        yield put(actions.fetchEodSucceeded(eod));
    } catch (e) {
        yield put(actions.fetchEodError(e.message));
    }
    try {
        const simmVersion = yield call(api.simmVersion)
        yield put(actions.fetchSimmVersionSucceeded(simmVersion));
    } catch (e) {
        yield put(actions.fetchSimmVersionError(e.message));
    }
}

/**
 * Handles the app initialization process.
 * 
 * This saga is triggered when the app component is loaded (LOADED action).
 * It fetches all initial data and sets the initialized flag when complete.
 * 
 * The initialization always completes (using finally block), even if data
 * fetching fails, to prevent the app from being stuck in loading state.
 * 
 * @generator
 * @yields {call} fetchCsasAndCounterparties to load initial data
 * @yields {put} setInitialised action to mark initialization complete
 * 
 * @example
 * // Triggered from component
 * useEffect(() => {
 *   dispatch(loaded());
 * }, []);
 * 
 * // Saga will:
 * // 1. Fetch all CSA data
 * // 2. Fetch EOD date
 * // 3. Fetch SIMM version
 * // 4. Set initialised = true
 */
export function* appLoaded() {
    try {
        yield call(fetchCsasAndCounterparties, systemInfoApi);
    } finally {
        try {
            const userRole = yield call(settingsApi.getUserRole);
            yield put(setUserRole(userRole));
        } catch (e) {
            // Non-fatal — role-based UI restrictions will default to most restrictive
        }
        yield put(actions.setInitialised(true));
    }
}


/**
 * Root saga that combines all app-level sagas.
 * 
 * Watchers:
 * - LOADED action → appLoaded saga
 * 
 * @generator
 * @yields {all} Array of saga watchers
 * 
 * @example
 * // Registered in rootSaga.js
 * yield spawn(appSagas);
 */
export default function* rootSaga() {
    yield all([
        takeEvery(actions.LOADED, appLoaded)
    ]);
}
